
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const topics = [
    'Policy Updates',
    'Research & Studies', 
    'Educational Resources',
    'Community Stories',
    'Event Announcements',
    'Industry News'
  ];

  const handleTopicChange = (topic: string) => {
    setSelectedTopics(prev => 
      prev.includes(topic)
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('topics', selectedTopics.join(', '));
      formData.append('subscription_type', 'newsletter');

      const response = await fetch('https://formsubmit.co/3e9da5b1-6d66-4b44-97b6-22d2bf6e6dc8', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setSelectedTopics([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-blue-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Never Miss Important Updates
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Get personalized news updates delivered to your inbox. Choose the topics that matter most to your practice.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-200 mb-1">25,000+</div>
                <div className="text-blue-100 text-sm">Active Subscribers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-200 mb-1">Weekly</div>
                <div className="text-blue-100 text-sm">Newsletter Updates</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-200 mb-1">48hrs</div>
                <div className="text-blue-100 text-sm">Breaking News Alerts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-200 mb-1">98%</div>
                <div className="text-blue-100 text-sm">Satisfaction Rate</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-8">
            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit}>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Subscribe to CPVA News</h3>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg">
                  Thank you for subscribing! You'll receive our next newsletter soon.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
                  There was an error processing your subscription. Please try again.
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  News Topics (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {topics.map((topic) => (
                    <label key={topic} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="topics"
                        value={topic}
                        checked={selectedTopics.includes(topic)}
                        onChange={() => handleTopicChange(topic)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{topic}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="breaking_news"
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Send me breaking news alerts and urgent policy updates
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !email}
                className="w-full bg-red-600 hover:bg-red-700 whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Subscribing...
                  </>
                ) : (
                  <>
                    <i className="ri-mail-line mr-2"></i>
                    Subscribe to Newsletter
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 mt-4 text-center">
                You can unsubscribe at any time. We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
